"use client";

import Link from "next/link";
import { FormEvent, useEffect, useMemo, useState } from "react";

type CountryOption = {
  code: string;
  name: string;
  dialCode: string;
};

type StateOption = {
  sigla: string;
  nome: string;
};

type CityOption = {
  nome: string;
};

type RestCountriesItem = {
  cca2?: string;
  name?: {
    common?: string;
  };
  idd?: {
    root?: string;
    suffixes?: string[];
  };
};

type GeoLocationResponse = {
  country_code?: string;
  country_calling_code?: string;
  region?: string;
  city?: string;
};

type RegisterErrorResponse = {
  message?: string | string[];
};

export default function CadastroPage() {
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("Brasil");
  const [countryCode, setCountryCode] = useState("BR");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("55");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");

  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [cities, setCities] = useState<CityOption[]>([]);

  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const [usernameEdited, setUsernameEdited] = useState(false);
  const [detectedRegionName, setDetectedRegionName] = useState("");
  const [detectedCityName, setDetectedCityName] = useState("");

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");

  const isBrazil = countryCode === "BR";

  useEffect(() => {
    async function loadCountries() {
      try {
        setLoadingCountries(true);

        const response = await fetch(
          "https://restcountries.com/v3.1/all?fields=name,cca2,idd",
        );

        if (!response.ok) {
          throw new Error("Falha ao carregar países.");
        }

        const data = (await response.json()) as RestCountriesItem[];

        const parsedCountries: CountryOption[] = data
          .map((item) => {
            const root = item.idd?.root ?? "";
            const suffix = Array.isArray(item.idd?.suffixes)
              ? item.idd?.suffixes?.[0] ?? ""
              : "";
            const dialCode = `${root}${suffix}`.replace(/\D/g, "");

            return {
              code: item.cca2 ?? "",
              name: item.name?.common ?? "",
              dialCode,
            };
          })
          .filter(
            (item) =>
              item.code.trim() !== "" && item.name.trim() !== "",
          )
          .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));

        setCountries(parsedCountries);

        const fallbackCountry =
          parsedCountries.find((item) => item.code === "BR") ??
          parsedCountries[0];

        if (fallbackCountry) {
          setCountry(fallbackCountry.name);
          setCountryCode(fallbackCountry.code);

          if (fallbackCountry.dialCode) {
            setPhoneCountryCode(fallbackCountry.dialCode);
          }
        }

        try {
          const geoResponse = await fetch("https://ipapi.co/json/");

          if (!geoResponse.ok) {
            throw new Error("Falha ao detectar localização.");
          }

          const geoData = (await geoResponse.json()) as GeoLocationResponse;

          const detectedCountry =
            parsedCountries.find(
              (item) =>
                item.code === String(geoData.country_code ?? "").toUpperCase(),
            ) ?? fallbackCountry;

          if (detectedCountry) {
            setCountry(detectedCountry.name);
            setCountryCode(detectedCountry.code);
          }

          const detectedDialCode = String(
            geoData.country_calling_code ?? "",
          ).replace(/\D/g, "");

          if (detectedDialCode) {
            setPhoneCountryCode(detectedDialCode);
          } else if (detectedCountry?.dialCode) {
            setPhoneCountryCode(detectedCountry.dialCode);
          }

          if (String(geoData.country_code ?? "").toUpperCase() === "BR") {
            setDetectedRegionName(String(geoData.region ?? "").trim());
            setDetectedCityName(String(geoData.city ?? "").trim());
          }
        } catch {
          // segue com o fallback padrão
        }
      } catch {
        setErrorMessage("Não foi possível carregar a lista de países.");
      } finally {
        setLoadingCountries(false);
      }
    }

    loadCountries();
  }, []);

  useEffect(() => {
    async function loadStates() {
      if (!isBrazil) {
        setStates([]);
        setCities([]);
        setState("");
        setCity("");
        return;
      }

      try {
        setLoadingStates(true);

        const response = await fetch(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome",
        );

        if (!response.ok) {
          throw new Error("Falha ao carregar estados.");
        }

        const data = (await response.json()) as StateOption[];
        setStates(data);
      } catch {
        setErrorMessage("Não foi possível carregar os estados.");
      } finally {
        setLoadingStates(false);
      }
    }

    loadStates();
  }, [isBrazil]);

  useEffect(() => {
    async function loadCities() {
      if (!isBrazil || !state) {
        setCities([]);
        setCity("");
        return;
      }

      try {
        setLoadingCities(true);

        const response = await fetch(
          `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state}/municipios?orderBy=nome`,
        );

        if (!response.ok) {
          throw new Error("Falha ao carregar cidades.");
        }

        const data = (await response.json()) as CityOption[];
        setCities(data);
      } catch {
        setErrorMessage("Não foi possível carregar as cidades.");
      } finally {
        setLoadingCities(false);
      }
    }

    loadCities();
  }, [isBrazil, state]);

  useEffect(() => {
    if (!isBrazil || !detectedRegionName || !states.length || state) {
      return;
    }

    const matchedState = states.find(
      (item) => normalizeText(item.nome) === normalizeText(detectedRegionName),
    );

    if (matchedState) {
      setState(matchedState.sigla);
    }
  }, [isBrazil, detectedRegionName, states, state]);

  useEffect(() => {
    if (!isBrazil || !detectedCityName || !cities.length || city) {
      return;
    }

    const matchedCity = cities.find(
      (item) => normalizeText(item.nome) === normalizeText(detectedCityName),
    );

    if (matchedCity) {
      setCity(matchedCity.nome);
    }
  }, [isBrazil, detectedCityName, cities, city]);

  const selectedCountry = useMemo(
    () => countries.find((item) => item.code === countryCode) ?? null,
    [countries, countryCode],
  );

  useEffect(() => {
    if (usernameEdited) {
      return;
    }

    setUsername(generateUsername(name));
  }, [name, usernameEdited]);

  function handleCountryChange(value: string) {
    const selected = countries.find((item) => item.code === value);

    setCountryCode(value);
    setCountry(selected?.name ?? "");
    setState("");
    setCity("");
    setCities([]);

    if (selected?.dialCode) {
      setPhoneCountryCode(selected.dialCode);
    }
  }

  function normalizeText(value: string) {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  }

  function formatPhone(value: string) {
    return value.replace(/\D/g, "").slice(0, 15);
  }

  function formatPhoneDisplay(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, 15);

    if (digits.length <= 2) {
      return digits;
    }

    if (digits.length <= 7) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    if (digits.length <= 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)} ${digits.slice(11)}`;
  }

  function generateUsername(value: string) {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9._\s-]/g, "")
      .trim()
      .replace(/\s+/g, ".")
      .replace(/\.{2,}/g, ".")
      .slice(0, 30);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name,
            username: username.trim().toLowerCase(),
            email,
            country,
            state,
            city,
            phoneCountryCode,
            phoneNumber,
            password,
          }),
        },
      );

      const data = (await response.json()) as RegisterErrorResponse;

      if (!response.ok) {
        setErrorMessage(
          Array.isArray(data.message)
            ? data.message.join(", ")
            : data.message || "Não foi possível criar a conta.",
        );
        return;
      }

      setSubmittedEmail(email.trim());

      setSuccessMessage(
        "Conta criada com sucesso. Verifique seu e-mail para ativar o acesso à plataforma.",
      );

      setName("");
      setUsername("");
      setUsernameEdited(false);
      setEmail("");
      setCountry(selectedCountry?.name ?? "Brasil");
      setCountryCode(selectedCountry?.code ?? "BR");
      setState("");
      setCity("");
      setPhoneNumber("");
      setPassword("");
    } catch {
      setErrorMessage("Erro de conexão com a API.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {successMessage ? (
        <div className="flex min-h-[520px] items-center justify-center">
          <div className="w-full max-w-2xl text-center">
            <div className="platform-text-muted text-sm">Cadastro concluído</div>

            <h1 className="platform-text mt-3 text-4xl font-semibold tracking-tight">
              Conta criada com sucesso
            </h1>
            <h1 className="platform-text mt-3 text-4xl font-semibold tracking-tight">
              {submittedEmail}
            </h1>

            <p className="platform-text-secondary mt-4 text-base leading-7">
              Enviamos um e-mail de verificação para você.
              Caso não encontre na caixa de entrada, verifique também as pastas de spam ou lixo eletrônico.
            </p>

            <div className="mt-8 flex justify-center">
              <Link
                href="https://trinitmusic.com.br"
                className="platform-button-primary inline-flex px-6 py-2 text-sm"
              >
                Voltar para o site
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-8">
            <div className="platform-text-muted text-sm">Novo cadastro</div>

            <h1 className="platform-text mt-3 text-4xl font-semibold tracking-tight">
              Criar conta na Platform
            </h1>

            <p className="platform-text-secondary mt-4 text-base leading-7">
              Preencha seus dados para começar a usar a plataforma.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <div className="mb-5">
                <h2 className="platform-text-muted text-sm font-semibold uppercase tracking-[0.18em]">
                  Dados de acesso
                </h2>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="platform-text-secondary mb-2 block text-sm font-medium">
                    Seu nome
                  </label>
                  <input
                    type="text"
                    required
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome completo"
                    className="platform-input"
                  />
                </div>

                <div>
                  <label className="platform-text-secondary mb-2 block text-sm font-medium">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    autoComplete="username"
                    value={username}
                    onChange={(e) => {
                      setUsernameEdited(true);
                      setUsername(
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9._-]/g, ""),
                      );
                    }}
                    placeholder="Ex: rodrigoegustavo"
                    className="platform-input"
                  />
                  <p className="platform-text-muted mt-2 text-xs">
                    Gerado automaticamente a partir do nome, mas você pode editar.
                  </p>
                </div>

                <div>
                  <label className="platform-text-secondary mb-2 block text-sm font-medium">
                    E-mail
                  </label>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="voce@exemplo.com"
                    className="platform-input"
                  />
                </div>

                <div>
                  <label className="platform-text-secondary mb-2 block text-sm font-medium">
                    Senha
                  </label>
                  <input
                    type="password"
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Crie uma senha"
                    className="platform-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="mb-5">
                <h2 className="platform-text-muted text-sm font-semibold uppercase tracking-[0.18em]">
                  Localização
                </h2>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="platform-text-secondary mb-2 block text-sm font-medium">
                    País
                  </label>
                  <select
                    required
                    value={countryCode}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    disabled={loadingCountries}
                    className="platform-input disabled:opacity-60"
                  >
                    <option value="">
                      {loadingCountries ? "Carregando países..." : "Selecione"}
                    </option>
                    {countries.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="platform-text-secondary mb-2 block text-sm font-medium">
                      Estado
                    </label>

                    {isBrazil ? (
                      <select
                        required
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        disabled={loadingStates}
                        className="platform-input disabled:opacity-60"
                      >
                        <option value="">
                          {loadingStates ? "Carregando estados..." : "Selecione o estado"}
                        </option>
                        {states.map((item) => (
                          <option key={item.sigla} value={item.sigla}>
                            {item.nome}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        required
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        placeholder="Digite seu estado / região"
                        className="platform-input"
                      />
                    )}
                  </div>

                  <div>
                    <label className="platform-text-secondary mb-2 block text-sm font-medium">
                      Cidade
                    </label>

                    {isBrazil ? (
                      <select
                        required
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={!state || loadingCities}
                        className="platform-input disabled:opacity-60"
                      >
                        <option value="">
                          {!state
                            ? "Selecione primeiro o estado"
                            : loadingCities
                              ? "Carregando cidades..."
                              : "Selecione a cidade"}
                        </option>
                        {cities.map((item) => (
                          <option key={item.nome} value={item.nome}>
                            {item.nome}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        required
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Digite sua cidade"
                        className="platform-input"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-5">
                <h2 className="platform-text-muted text-sm font-semibold uppercase tracking-[0.18em]">
                  Contato
                </h2>
              </div>

              <div className="grid gap-5 md:grid-cols-[120px_minmax(0,1fr)]">
                <div>
                  <label className="platform-text-secondary mb-2 block text-sm font-medium">
                    DDI
                  </label>
                  <input
                    type="text"
                    required
                    value={phoneCountryCode}
                    onChange={(e) =>
                      setPhoneCountryCode(
                        e.target.value.replace(/\D/g, "").slice(0, 4),
                      )
                    }
                    placeholder="55"
                    className="platform-input"
                  />
                </div>

                <div>
                  <label className="platform-text-secondary mb-2 block text-sm font-medium">
                    Celular
                  </label>
                  <input
                    type="tel"
                    required
                    autoComplete="tel"
                    value={formatPhoneDisplay(phoneNumber)}
                    onChange={(e) => setPhoneNumber(formatPhone(e.target.value))}
                    placeholder="(14) 99999-9999"
                    className="platform-input"
                  />
                </div>
              </div>
            </div>

            {errorMessage ? <div className="platform-alert-error">{errorMessage}</div> : null}

            <button
              type="submit"
              disabled={loading}
              className="platform-button-primary"
            >
              {loading ? "Criando conta..." : "Criar conta"}
            </button>
          </form>

          <div className="platform-divider platform-text-secondary mt-8 pt-6 text-sm">
            Já tem uma conta?{" "}
            <Link href="/login" className="platform-text transition">
              Entrar
            </Link>
          </div>
        </>
      )}
    </div>
  );
}